/**
 * Document-selection comparison between the two pipelines.
 *
 * The old view showed three separate buckets ("Both models", "Only baseline",
 * "Only candidate"), which made it impossible to tell at a glance whether the
 * Eval model had *lost* a document the Base model picked up. Here every document
 * is one row that reports its presence on each side, and dropping a document is
 * called out as the regression it is.
 */

/** Eval missed a document Base selected — the case that needs a human. */
export const DROPPED_BY_EVAL = "droppedByEval";

/** Eval selected a document Base ignored — worth a look, not a regression. */
export const ADDED_BY_EVAL = "addedByEval";

/** Both pipelines agreed. */
export const IN_BOTH = "inBoth";

const STATUS_ORDER = {
  [DROPPED_BY_EVAL]: 0,
  [ADDED_BY_EVAL]: 1,
  [IN_BOTH]: 2,
};

function toStatus(isInBase, isInEval) {
  if (isInBase && isInEval) {
    return IN_BOTH;
  }

  return isInBase ? DROPPED_BY_EVAL : ADDED_BY_EVAL;
}

function resolveName(doc, id) {
  return doc?.name || doc?.fileName || `Unresolved document ${id.slice(0, 8)}`;
}

/**
 * @param {Object} input
 * @param {string[]} input.baselineIds - documents the Base pipeline selected
 * @param {string[]} input.candidateIds - documents the Eval pipeline selected
 * @param {Object} [input.docsById] - id → document metadata, for names
 * @returns {{rows: Object[], counts: Object, overlap: number|null}}
 */
export function buildDocComparison({
  baselineIds = [],
  candidateIds = [],
  docsById = {},
}) {
  const baseSet = new Set(baselineIds.map(String));
  const evalSet = new Set(candidateIds.map(String));
  const allIds = [...new Set([...baseSet, ...evalSet])];

  const rows = allIds
    .map((id) => {
      const isInBase = baseSet.has(id);
      const isInEval = evalSet.has(id);
      const doc = docsById[id] || null;

      return {
        id,
        doc,
        name: resolveName(doc, id),
        isInBase,
        isInEval,
        status: toStatus(isInBase, isInEval),
      };
    })
    .sort((left, right) => {
      const byStatus = STATUS_ORDER[left.status] - STATUS_ORDER[right.status];
      if (byStatus !== 0) {
        return byStatus;
      }

      return left.name.localeCompare(right.name);
    });

  const counts = {
    total: rows.length,
    base: baseSet.size,
    eval: evalSet.size,
    inBoth: rows.filter((row) => row.status === IN_BOTH).length,
    dropped: rows.filter((row) => row.status === DROPPED_BY_EVAL).length,
    added: rows.filter((row) => row.status === ADDED_BY_EVAL).length,
  };

  const union = allIds.length;
  const overlap = union === 0 ? null : counts.inBoth / union;

  return { rows, counts, overlap };
}

/**
 * One sentence a reviewer can read instead of doing arithmetic on the counts.
 */
export function describeDocComparison(counts) {
  if (!counts || counts.total === 0) {
    return "Neither pipeline selected any documents.";
  }
  if (counts.dropped === 0 && counts.added === 0) {
    return `Both pipelines selected the same ${counts.inBoth} document${
      counts.inBoth === 1 ? "" : "s"
    }.`;
  }

  const parts = [];
  if (counts.dropped > 0) {
    parts.push(
      `Eval dropped ${counts.dropped} document${counts.dropped === 1 ? "" : "s"} Base selected`,
    );
  }
  if (counts.added > 0) {
    parts.push(
      `Eval added ${counts.added} document${counts.added === 1 ? "" : "s"} Base ignored`,
    );
  }

  return `${parts.join(" · ")}. ${counts.inBoth} matched.`;
}
